/**
 * Atlas PM API 客户端
 *
 * 统一处理：
 *   - baseURL（开发期走 Vite 代理 /api → http://127.0.0.1:8082）
 *   - Authorization 头（从 token provider 取 token；默认读 localStorage）
 *   - 统一响应解包：{ data } → 直接返回 data；{ error } → 抛 ApiError
 *   - 401 自动清 token + 跳登录页
 *   - 网络错误转成友好中文提示
 *
 * ⭐ 这是 p3-map / p3-star / p3-detail 共同依赖的接口，请勿随意改动签名。
 *    新增能力请以「可选参数 / 新导出」方式扩展，避免破坏调用方。
 *
 * 用法：
 *   import { api, qs } from '../api/client';
 *   const page = await api.get<Paginated<Project>>(`/projects${qs({ page: 1 })}`);
 */

import type { ApiErrorBody, ValidationIssue } from './types';

/** localStorage 键名（auth store 与 client 共用，避免循环依赖） */
export const STORAGE_KEYS = {
  token: 'atlaspm_token',
  user: 'atlaspm_user',
} as const;

const API_BASE = '/api';

/**
 * 统一错误类型。
 * 调用方可用 `instanceof ApiError` 判断，并读取 code / status / message。
 */
export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly detail?: unknown,
    /** P6.28：参数校验失败时的逐字段明细 */
    public readonly details?: ValidationIssue[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/* ─────────────────────────────────────────────
   可注入的 token 提供者 / 401 回调
   （由 stores/auth 在模块加载时注册，避免 client ↔ store 循环依赖）
   ───────────────────────────────────────────── */

function readTokenFromStorage(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEYS.token);
  } catch {
    return null;
  }
}

let tokenGetter: () => string | null = readTokenFromStorage;

/** 注册 token 获取函数（auth store 调用） */
export function setTokenGetter(fn: () => string | null): void {
  tokenGetter = fn;
}

let unauthorizedHandler: (() => void) | null = null;

/** 注册 401 回调（auth store 调用，用于清空内存态） */
export function setUnauthorizedHandler(fn: () => void): void {
  unauthorizedHandler = fn;
}

/** 清空本地登录信息并跳转登录页（幂等，避免在登录页重复跳转） */
function handleUnauthorized(): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.token);
      localStorage.removeItem(STORAGE_KEYS.user);
    }
  } catch {
    /* ignore storage errors */
  }
  try {
    unauthorizedHandler?.();
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

export interface RequestOptions {
  signal?: AbortSignal;
}

/**
 * 底层请求。一般不用直接调用，优先使用下面的 `api` 对象。
 *
 * @throws {ApiError} 任何非 2xx、解析失败或网络异常
 */
export async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = tokenGetter();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: options?.signal,
    });
  } catch (err) {
    // 主动取消不应被当成网络错误
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    if (err instanceof Error && err.name === 'AbortError') throw err;
    throw new ApiError('NETWORK_ERROR', '网络连接失败，请检查后端服务', 0);
  }

  // 204 / 空响应
  const text = await res.text();
  let json: unknown = undefined;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = undefined;
    }
  }

  if (res.ok) {
    // ⚠️ P6.6 防御：2xx 响应体里出现 error 字段说明服务端契约异常，必须显式失败。
    //    历史缺陷：storage/confirm 校验失败时返回 HTTP 201 + { error }，
    //    这里会把失败当成功返回，真实错误原因（如文件类型不允许）被完全吞掉。
    if (
      json &&
      typeof json === 'object' &&
      'error' in (json as Record<string, unknown>) &&
      !('data' in (json as Record<string, unknown>))
    ) {
      const err = (json as { error?: { code?: unknown; message?: unknown } }).error;
      throw new ApiError(
        typeof err?.code === 'string' ? err.code : 'UNEXPECTED_ERROR_BODY',
        typeof err?.message === 'string'
          ? err.message
          : `服务端返回了异常响应体（HTTP ${res.status}）`,
        res.status,
      );
    }
    // 成功：优先解包 { data }；否则原样返回（如 /health 直接返回对象）
    if (json && typeof json === 'object' && 'data' in (json as Record<string, unknown>)) {
      return (json as { data: T }).data;
    }
    return json as T;
  }

  // 失败：解析 { error: { code, message } }
  let code = `HTTP_${res.status}`;
  let message = `请求失败（HTTP ${res.status}）`;
  let detail: unknown;
  let details: ValidationIssue[] | undefined;
  const errBody = json as Partial<ApiErrorBody> | undefined;
  if (errBody?.error && typeof errBody.error === 'object') {
    if (typeof errBody.error.code === 'string') code = errBody.error.code;
    if (typeof errBody.error.message === 'string') message = errBody.error.message;
    detail = errBody.error.detail;
    if (Array.isArray(errBody.error.details)) details = errBody.error.details;
  } else if (json && typeof json === 'object' && 'message' in (json as Record<string, unknown>)) {
    const m = (json as { message?: unknown }).message;
    if (typeof m === 'string') message = m;
    else if (Array.isArray(m) && typeof m[0] === 'string') message = m.join('；');
  }

  if (res.status === 401) {
    handleUnauthorized();
    // 401 一律用统一话术（后端 message 已足够友好时保留）
    if (!message || message.startsWith('请求失败')) message = '登录已过期，请重新登录';
  }

  throw new ApiError(code, message, res.status, detail, details);
}

/** 便捷方法集合 */
export const api = {
  get: <T>(path: string, opt?: RequestOptions) => request<T>('GET', path, undefined, opt),
  post: <T>(path: string, body?: unknown, opt?: RequestOptions) =>
    request<T>('POST', path, body, opt),
  patch: <T>(path: string, body?: unknown, opt?: RequestOptions) =>
    request<T>('PATCH', path, body, opt),
  put: <T>(path: string, body?: unknown, opt?: RequestOptions) =>
    request<T>('PUT', path, body, opt),
  delete: <T>(path: string, opt?: RequestOptions) => request<T>('DELETE', path, undefined, opt),
};

/**
 * 把对象拼成查询串，忽略 undefined / null / 空字符串。
 * 数组会展开为同名重复参数。
 *
 * qs({ page: 1, keyword: '', provinceCode: undefined }) → '?page=1'
 * qs({ provinceCodes: ['330000','110000'] })           → '?provinceCodes=330000&provinceCodes=110000'
 */
export function qs(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    const k = encodeURIComponent(key);
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === '') continue;
        parts.push(`${k}=${encodeURIComponent(String(item))}`);
      }
    } else {
      parts.push(`${k}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.length ? `?${parts.join('&')}` : '';
}
